import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Icon, Modal, Message, Table, Dimmer, Loader } from "semantic-ui-react";
import { DataTable, getCommonAPI } from "hpp-fe-common";

import { signOut } from "../../../auth/authSlice";

const DETAILS_API_PATH = "/api_v1/points/my-claimed-history/details";

const serialColumns = [
  { name: "Serial Number", selector: (row) => row.serialNumber, sortable: true, grow: 2 },
  { name: "SAP Code", selector: (row) => row.sapCode, sortable: true },
  { name: "CN Points", selector: (row) => row.cnPoints, sortable: true },
  { name: "Transaction Points", selector: (row) => row.transactionPoints, sortable: true },
  { name: "Total Points", selector: (row) => row.totalPoints, sortable: true },
];

const filterTypes = [
  { key: "serialNumber", text: "By Serial Number", value: "serialNumber" },
  { key: "sapCode", text: "By SAP Code", value: "sapCode" },
];

function ClaimedHistoryModal({ open, onClose, selectedRow }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageData, setErrorMessageData] = useState("");
  const [header, setHeader] = useState(null);
  const [serials, setSerials] = useState([]);

  const resetState = useCallback(() => {
    setLoading(false);
    setError(false);
    setErrorMessage("");
    setErrorMessageData("");
    setHeader(null);
    setSerials([]);
  }, []);

  const fetchDetails = useCallback(() => {
    if (!selectedRow?.upstreamCompanyId || !selectedRow?.redeemAt) return;

    setLoading(true);
    setError(false);
    setErrorMessage("");
    setErrorMessageData("");

    const requestURL = encodeURI(
      `${DETAILS_API_PATH}?portal=inst&upstreamCompanyId=${selectedRow.upstreamCompanyId}&redeemAt=${selectedRow.redeemAt}`
    );

    getCommonAPI()
      .get(requestURL)
      .then(({ data }) => {
        if (data?.authenticated === false) {
          return dispatch(
            signOut({
              error: true,
              errorMessage: data.errorMessage,
              errorMessageData: data.errorMessageData,
            })
          );
        }

        setLoading(false);

        if (data?.error) {
          setError(true);
          setErrorMessage(data?.errorMessage || "Error loading details");
          setErrorMessageData(data?.errorMessageData || "");
          setHeader(null);
          setSerials([]);
        } else {
          setHeader(data?.data?.header || null);
          setSerials(data?.data?.serials || []);
        }
      })
      .catch((err) => {
        setLoading(false);
        setError(true);
        setErrorMessage("Error loading claimed serials. Please contact system administrator");
        setErrorMessageData(err.toString());
        setHeader(null);
        setSerials([]);
      });
  }, [dispatch, selectedRow]);

  useEffect(() => {
    if (open && selectedRow) {
      fetchDetails();
    }
  }, [open, selectedRow, fetchDetails]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} size="large" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="eye" /> Claimed Serial Details
      </Modal.Header>

      <Modal.Content scrolling>
        <Dimmer active={loading} inverted>
          <Loader>Loading...</Loader>
        </Dimmer>

        {error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}</p>
            {errorMessageData && <p>{typeof errorMessageData === 'string' ? errorMessageData : JSON.stringify(errorMessageData)}</p>}
          </Message>
        )}

        {header && !error && (
          <Table basic="very" celled style={{ maxWidth: "500px", marginBottom: "20px" }}>
            <Table.Body>
              <Table.Row>
                <Table.Cell><strong>Redeemed Company</strong></Table.Cell>
                <Table.Cell>{header.upstreamCompanyName || "-"}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Redeemed Time </strong></Table.Cell>
                <Table.Cell>{header.redeemAt ? new Date(header.redeemAt).toLocaleString() : "-"}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Serial Count</strong></Table.Cell>
                <Table.Cell>{header.serialCount}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Total Points</strong></Table.Cell>
                <Table.Cell><b>{header.totalRedeemPoints}</b></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        )}

        {!error && Array.isArray(serials) && serials.length > 0 && (
          <DataTable
            columns={serialColumns}
            data={serials}
            filterFromServer={false}
            filterCaption="Search serials..."
            filterTypes={filterTypes}
            showTotalFor="totalPoints"
          />
        )}

        {!error && !loading && Array.isArray(serials) && serials.length === 0 && (
          <Message info>
            <p>No serials found for this company.</p>
          </Message>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose}>
          <Icon name="close" /> Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default ClaimedHistoryModal;
